import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, TrendingUp, DollarSign, Users } from "lucide-react";
import { Link } from "react-router-dom";
import caseStudy1Image from "@/assets/case-study-digital-value-model-opt.webp";
import caseStudy2Image from "@/assets/case-study-dtc-growth-opt.webp";
import caseStudy3Image from "@/assets/case-study-member-portal-opt.webp";

const caseStudies = [
  {
    id: 1,
    icon: DollarSign,
    category: "Strategy",
    title: "The $10M Digital Value Model",
    subtitle: "Revenue Attribution & Cost Deflection",
    impact: "$10M Annual Impact",
    description:
      "Moving beyond 'pageviews' to a P&L-based attribution model. How I quantified the value of digital behaviors to prove $10M in annual impact.",
    tags: ["Strategy"],
    color: "primary",
    href: "/case-studies/digital-transformation",
    image: caseStudy1Image,
  },
  {
    id: 2,
    icon: TrendingUp,
    category: "Growth",
    title: "The Direct-to-Consumer Growth Engine",
    subtitle: "Scaling Off-Exchange Revenue to $6M",
    impact: "$6M Revenue",
    description: "Breaking reliance on state exchanges and high-cost brokers. Led the strategic RFP and platform launch that opened a new sales channel, reducing CAC and positioning digital as a primary revenue driver.",
    tags: ["Growth", "E-commerce", "Strategy"],
    color: "accent",
    href: "/case-studies/aca-portal",
    image: caseStudy2Image,
  },
  {
    id: 3,
    icon: Users,
    category: "CX Modernization",
    title: "Enterprise Member Portal",
    subtitle: "Strategic Build vs. Buy",
    impact: "$3.5M Savings",
    description:
      "Led the financial analysis to pivot from a legacy SaaS vendor to a custom cloud platform. Projected to save $3.5M in TCO over 5 years while driving $800k in annual operational deflection.",
    tags: ["TCO Analysis", "Build vs Buy", "Cost Deflection"],
    color: "primary",
    href: "/case-studies/member-portal",
    image: caseStudy3Image,
  },
];

export function CaseStudies() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="case-studies" ref={ref} aria-labelledby="case-studies-heading" className="py-20 md:py-28 bg-secondary/50">
      <div className="container-wide">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mb-12"
        >
          <h2 id="case-studies-heading" className="text-sm font-semibold uppercase tracking-wider text-accent mb-3">Case Studies</h2>
          <h3 className="text-3xl md:text-4xl font-bold text-foreground mb-4">Transformations That Delivered</h3>
          <p className="text-lg text-muted-foreground">
            Deep dives into high-impact projects that drove measurable business results.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {caseStudies.map((study, index) => (
            <Link key={study.id} to={study.href}>
              <motion.article
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`group relative bg-card rounded-2xl overflow-hidden border border-border shadow-card card-interactive cursor-pointer h-full flex flex-col ${
                  index === 0 ? "lg:row-span-1" : ""
                }`}
              >
                {/* Image */}
                {study.image && (
                  <div className="relative h-40 overflow-hidden">
                    <img 
                      src={study.image} 
                      alt={study.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-card via-transparent to-transparent" />
                  </div>
                )}

                <div className="p-6 md:p-8 flex flex-col flex-1">

                  {/* Impact Badge */}
                  <div
                    className={`inline-flex px-3 py-1.5 rounded-full text-sm font-bold mb-3 w-fit ${
                      study.color === "accent" ? "bg-accent/10 text-accent" : "bg-primary/10 text-primary"
                    }`}
                  >
                    {study.impact}
                  </div>

                  {/* Title */}
                  <h4 className="text-xl font-bold text-foreground mb-1 group-hover:text-primary transition-colors">
                    {study.title}
                  </h4>

                  {/* Subtitle */}
                  {study.subtitle && (
                    <p className="text-sm font-medium text-muted-foreground mb-3">{study.subtitle}</p>
                  )}

                  {/* Description */}
                  <p className="text-muted-foreground text-sm leading-relaxed mb-6 flex-1">{study.description}</p>

                  {/* Tags */}
                  <div className="flex flex-wrap gap-2 mb-6">
                    {study.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 text-xs font-medium rounded-md bg-secondary text-secondary-foreground"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  {/* CTA Link */}
                  <div className="flex items-center gap-2 text-sm font-semibold text-primary group-hover:gap-3 transition-all" aria-hidden="true">
                    Read Case Study
                    <ArrowRight className="w-4 h-4" />
                  </div>
                  <span className="sr-only">Read case study: {study.title}</span>
                </div>

                {/* Hover border effect */}
                <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-primary/20 transition-colors pointer-events-none" />
              </motion.article>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
